import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AddressField } from '../../../../../global/components/form-element/address';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { esGenderOptions, GenderOptions, YesNoOptions } from '../../../../../global/constants/lists';
import { parseLov } from '../../../../../global/helpers/form-data-helper';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { EmailInfo } from '../../../../account-info/features/personal-info/email-info';
import { BeneficiaryPersonMailingAddressRequiredSchema } from '../../../../account-info/features/personal-info/personal-form-models';
import { PhoneInfo } from '../../../../account-info/features/personal-info/phone-info';
import { MailingAddressFieldNames } from '../../../../account-info/models/form-models';
import { getAddressLabels } from '../../../../account-info/selectors/form-selectors';
import { BeneficiaryCreateFormModel, RelationshipType } from '../model/beneficiary-create-models';
import { BeneficiarySettings } from '../model/epi-props';
import { GetMappedRelationshipOptions } from './helper/create-beneficiary-helper';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { ArcRoutes } from '../../../constants/routes';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { personInfoSelfDescribeValue } from '../../../../../global/validation-schemas/account-information-form-validation';


interface Props {
	settings: BeneficiarySettings;
	formProps: FormikProps<BeneficiaryCreateFormModel>;
	showGenderIdentity?: boolean;
}
export function CreateBeneficiaryPersonFields(props: Props) {

	const { settings, formProps, showGenderIdentity } = props;
	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const suffix = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.suffix);
	const language = useSelector<TcsmStates, string>(state => state.globalization?.currentlanguage);
	const mappedSuffix = suffix ? parseLov(suffix) : [];
	const mappedRelationship = GetMappedRelationshipOptions(settings?.relationshipCodesMapping);
	const buttonClasses = buttonStyles();
	const history = useHistory();
	const isSpouseOrDomesticPartner = formProps?.values.relationship === RelationshipType.Spouse || formProps?.values.relationship === RelationshipType.Domestic;
	const isChild = formProps?.values.relationship === RelationshipType.StepChild || formProps?.values.relationship === RelationshipType.Child;
	const relationshipLink = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.RelationshipsSegment}`);
	const beneficiaryLink = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.Beneficiary}`);
	const myCpgHome = useBasePath(AccountInfoRoutes.MyCpgRoot);

	const salutations = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.salutation_designation);
	const mappedSalutation = salutations ? parseLov(salutations) : [];

	const genderLov = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.gender_identity);
	const parsedGenders = genderLov ? parseLov(genderLov) : [];

	const redirectToPage = (url: string) => {
		history.push(url);
	};

	const onGenderIdChange = (e, fieldArrayProps: FieldArrayRenderProps) => {
		const values = formProps.values.gender_identity;
		const { value } = e.target;
		if (e.target.checked) {
			fieldArrayProps.push(value);
		} else {
			fieldArrayProps.remove(values.indexOf(value));
		}
	};

	return (
		<>
			<FormGroup>
				<DividerSection
					sectionText={settings?.personSectionTitle}
					removeDivider={isSpouseOrDomesticPartner}
				>
					<ContentSpacer>
						<h2>{settings?.personInformationSectionText}</h2>
					</ContentSpacer>
					<FormField
						name='relationship'
						label={settings?.relationshipLabel}
						component='input'
						options={mappedRelationship}
						errors={formProps.errors}
						touched={formProps.touched}
						select
						tooltip={{
							title: settings?.relationshipHelperText,
						}}
						required
					/>
					{
						isSpouseOrDomesticPartner &&
						<>
							<ContentSpacer>
								<Banner
									banner={{
										type: 'warning',
										label: settings.addSpouseOrDomesticPartnerWarningMessage,
									}}
								/>
							</ContentSpacer>
							<ContentSpacer>
								<WysiwygWithReactLink content={settings?.addNewSpouseNextStep} />
								<button className={buttonClasses.secondaryButton} onClick={() => redirectToPage(relationshipLink)} type='button'>{settings?.addNewSpouseButtonText}</button>
							</ContentSpacer>
							<ContentSpacer>
								<WysiwygWithReactLink content={settings?.addExistingSpouseMessage} />
								<button className={buttonClasses.secondaryButton} onClick={() => redirectToPage(beneficiaryLink)} type='button'>{settings?.addExistingSpouseButtonText}</button>
							</ContentSpacer>
							<ContentSpacer>
								<ButtonWrapper flexEnd>
									<button className={buttonClasses.primaryButton} onClick={() => redirectToPage(myCpgHome)} type='button'>{settings?.myCpgHomeText}</button>

								</ButtonWrapper>
							</ContentSpacer>
						</>
					}

					{ 
						isChild &&
						<>
							<FormField
								name='disabled_child_indicator'
								label={settings?.disabledChildLabel}
								component="radioGroup"
								options={YesNoOptions}
								touched={formProps.touched}
								errors={formProps.errors}
								required
							/> 
						</>
					}										
					{
						!isSpouseOrDomesticPartner &&
						<>
							<FormField
								name='designation_salutation'
								label={settings?.salutationLabel}
								component='input'
								options={mappedSalutation}
								errors={formProps.errors}
								touched={formProps.touched}
								select						
							/>
							<FormField
								name='first_name'
								label={settings?.legalFirstNameLabel}
								component='input'
								touched={formProps.touched}
								errors={formProps.errors}
								required
								tooltip={{
									title: settings.legalFirstNameHelperText,
								}}
							/>
							<FormField
								name='middle_name'
								label={settings.legalMiddleNameLabel}
								component='input'
								touched={formProps.touched}
								errors={formProps.errors}
								tooltip={{
									title: settings.legalMiddleNameHelperText,
								}}
							/>
							<FormField
								name='last_name'
								label={settings.legalLastNameLabel}
								component='input'
								touched={formProps.touched}
								errors={formProps.errors}
								required
								tooltip={{
									title: settings?.legalLastNameHelperText,
								}}
							/>
							<FormField
								name='suffix'
								label={labelSetting.suffixLabel}
								component='input'
								options={mappedSuffix}
								errors={formProps.errors}
								touched={formProps.touched}
								select
							/>
							<FormField
								name='perferred_name'
								label={labelSetting?.preferredLabel}
								component='input'
								touched={formProps.touched}
								errors={formProps.errors}
							/>
							<div className='form-item'>
								<ContentSpacer>
									<FormField
										name='gender'
										label={labelSetting.genderLabel}
										component='radioGroup'
										options={language === 'en' ? GenderOptions : esGenderOptions}
										touched={formProps.touched}
										errors={formProps.errors}
										tooltip={{
											title: labelSetting.genderTooltipMessage,
										}}
										required
									/>
								</ContentSpacer>
							</div>
							{
								showGenderIdentity &&
								<div className='form-item'>
									<FieldArray
										name='gender_identity'
										render={(fieldArrayProps) => (
											<>
												<ContentSpacer>
													<h5>{labelSetting.genderIdentityLabel}</h5>
													<WysiwygWithReactLink content={labelSetting.genderIdentityDescription} />
												</ContentSpacer>

												{
													parsedGenders.map(gender => {
														return (
															<FormField
																id={gender.value}
																name='gender_identity'
																label={gender.text}
																component='checkBox'
																touched={formProps.touched}
																errors={formProps.errors}
																value={gender.value}
																checked={formProps.values.gender_identity?.includes(gender.value)}
																checkBoxGroup
																onChange={(e) => onGenderIdChange(e, fieldArrayProps)}

															/>
														);
													})
												}
											</>
										)}
									/>
									{
										formProps.values.gender_identity?.includes(personInfoSelfDescribeValue) &&
										<FormField
											name='gender_identity_other'
											label={labelSetting?.genderIdentitySelfDescribeLabel || 'Provide Description Below'}
											component='input'
											touched={formProps.touched}
											errors={formProps.errors}
											required
										/>
									}
								</div>
							}
							<div className='form-item'>
								<ContentSpacer>
									<WysiwygWithReactLink content={settings?.dobSsnMessage} />
								</ContentSpacer>
							</div>
							<FormField
								name='date_of_birth'
								label={labelSetting.dobLabel}
								component='datePicker'
								touched={formProps.touched}
								errors={formProps.errors}
								tooltip={{
									title: labelSetting.tooltipMessage,
								}}
								autoWidth
								required
							/>
							<FormField
								name='tax_id'
								label={labelSetting.ssnTinLabel}
								component='input'
								touched={formProps.touched}
								errors={formProps.errors}
								mask={[/[0-9]|X/, /\d|X/, /\d|X/, '-', /\d|X/, /\d|X/, '-', /\d/, /\d/, /\d/, /\d/]}
								autoWidth
								tooltip={{
									title: labelSetting.tooltipMessage,
								}}
								required
							/>
						</>
					}
				</DividerSection>
			</FormGroup>
			{
				!isSpouseOrDomesticPartner &&
				<>
					<DividerSection sectionText={labelSetting.mailingAddressTitle} >
						<div className='form-item'>
							<AddressField
								currentCountry={formProps?.values?.mailing_country === '' ? 'US' : formProps?.values?.mailing_country}
								labels={getAddressLabels(labelSetting)}
								addressFieldNames={MailingAddressFieldNames}
								formProps={formProps}
								requiredSchema={BeneficiaryPersonMailingAddressRequiredSchema}
								person
							/>
						</div>
					</DividerSection>
					<PhoneInfo
						labelSetting={labelSetting}
						formProps={formProps}
						hideBusiness
					/>
					<EmailInfo
						labelSetting={labelSetting}
						formProps={formProps}
						hideDescription
						hideBusiness
					/>
				</>
			}
		</>
	);
}

CreateBeneficiaryPersonFields.defaultProps = {
	showGenderIdentity: false,	
};